import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FormContainer,
    DataForm,
    DataFormDetail,
    footerHeight,
    formHeaderHeight,
    headerHeight,
    PageContainerActionParamType,
    Loader,
    DataFormFieldChangeParamType,
    SaveDataParamType,
    useStateContext,

} from "@vegi.toenlioe/novitate-react-components";
import Role from '../../../models/Role';
import { Checkbox, FormControlLabel, Grid, GridOwnProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import RoleMenu from '../../../models/RoleMenu';
import { validate as uuidValidate } from "uuid";
import { MenuItems } from '../../../utils/Menu';

const groupName: string = "Umum";
const subgroupName: string = "Pengaturan";
const code: string = "GLOBAL-ROLE";
const title: string = "Role";
const endpoint = "role";
const menuEndpoint = "menu";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Role>({ kode: '', nama: '', spesifik_gudang: false, aktif: true });
    const [freeMenuList, setFreeMenuList] = useState<RoleMenu[]>([]);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
        {
            setLoading(true);
            apiCaller?.get(`${menuEndpoint}/all`)
                .then((response) => {
                    console.log({response});
                    setFreeMenuList(response.data?.data ? response.data.data : []);
                })
                .catch((error) => console.error({error}))
                .finally(() => setLoading(false))
            ;

            return;
        }

		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
                // console.log({response});
                setFormData({...response.data?.data});
                setFreeMenuList(response.data?.data ? response.data.data.list_all_menu : []);
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onDetailMenuChanged = useCallback((valueObj: any) => {
        const {fieldName, value, keyFieldName, keyFieldValue} = valueObj;
        const currentData = formData.list_selected_menu ?? [];
        const selectedFreeMenu = freeMenuList.filter(m => m.ucode_menu === keyFieldValue)[0];

        if (fieldName === "can_view")
        {
            if (value)
            {
                currentData.push({
                    ...selectedFreeMenu,
                    can_view: true,
                    can_add: false,
                    can_edit: false,
                    can_delete: false,
                    see_other_div: false,
                });
                setFormData({
                    ...formData,
                    list_selected_menu: currentData,
                });
            }

            if (!value)
            {
                const currentItems = currentData.filter((d: any) => d && d[keyFieldName] === keyFieldValue);
                const currentItem = currentItems.length ? currentItems[0] : null;

                if (currentItem)
                {
                    currentData.splice(currentData.indexOf(currentItem), 1);
                    setFormData({
                        ...formData,
                        list_selected_menu: currentData,
                    });
                }
            }

            return;
        }

        if (!currentData.length)
        {
            currentData.push({
                ...selectedFreeMenu,
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                see_other_div: false,
                [fieldName]: value,
            });
            setFormData({
                ...formData,
                list_selected_menu: currentData,
            });

            return;
        }

        const currentItems = currentData.filter((d: RoleMenu) => d && d.ucode_menu === keyFieldValue);
        const currentItem = currentItems.length ? currentItems[0] : null;

        if (!currentItem)
        {
            currentData.push({
                ...selectedFreeMenu,
                can_view: false,
                can_add: false,
                can_edit: false,
                can_delete: false,
                see_other_div: false,
                [fieldName]: value,
            });
            setFormData({
                ...formData,
                list_selected_menu: currentData,
            });

            // console.log({keyFieldName, keyFieldValue, currentData});
            return;
        }

        const newItem = {
            ...currentItem,
            [fieldName]: value,
        };
        currentData.splice(currentData.indexOf(currentItem), 1, newItem);
        setFormData({
            ...formData,
            list_selected_menu: currentData,
        });
        // console.log({keyFieldName, keyFieldValue, currentData});
    }, [formData, freeMenuList]);

    useEffect(() => {
        // console.log({formData});
    }, [formData?.list_selected_menu]);

    const onValidate = useCallback((data: Role | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data.kode)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        // console.log({pathObject});
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Role> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const dataToSend = {
            ucode: param.data.ucode,
            kode: param.data.kode,
            nama: param.data.nama,
            level: param.data.level,
            spesifik_gudang: param.data.spesifik_gudang,
            aktif: param.data.aktif,
            keterangan: param.data.keterangan,
            list_menu: param.data.list_selected_menu ? param.data.list_selected_menu.map(m => ({ucode_menu: m.ucode_menu, can_add: m.can_add, can_edit: m.can_edit, can_delete: m.can_delete, see_other_div: m.see_other_div})) : [],
        }

        const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    const menuListContainerProps: GridOwnProps =
        isMobile ?
        {
            item: true,
            lg: 4,
            md: 12,
            sx: {
                marginTop: "0.5rem",
            }
        }
        :
        {
            item: true,
            lg: 4,
            md: 12,
            sx: {
                marginTop: "0.5rem",
                height: `calc(100vh - ${headerHeight + formHeaderHeight + footerHeight + (isMobile ? headerHeight : 0)}px)`,
                '@supports (maxHeight: 100dvh)': {
                    height: `calc(100dvh - ${headerHeight + formHeaderHeight + footerHeight + (isMobile ? headerHeight : 0)}px)`,
                },
            }
        };

    const menuListTitleProps: GridOwnProps =
        {
            item: true,
            lg: 12,
            sx: {
                height: `${formHeaderHeight}px`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
            }
        };

    const menuListProps: GridOwnProps =
        isMobile ?
        {
            item: true,
            lg: 12,
            sx: {
                overflowY: "scroll",
                marginTop: "0.5rem",
            }
        }
        :
        {
            item: true,
            lg: 12,
            sx: {
                overflowY: "scroll",
                marginTop: "0.5rem",
                height: `calc(100vh - 1rem - ${headerHeight + formHeaderHeight + formHeaderHeight + footerHeight + (isMobile ? headerHeight : 0)}px)`,
                '@supports (maxHeight: 100dvh)': {
                    height: `calc(100dvh - 1rem - ${headerHeight + formHeaderHeight + formHeaderHeight + footerHeight + (isMobile ? headerHeight : 0)}px)`,
                },
            }
        };

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <Grid
                    container
                    spacing={2}
                >
                    <Grid
                        item
                        lg={8}
                        md={12}
                    >
                        <DataForm<Role>
                           
                            menuItems={MenuItems}
                            groupName={groupName}
                            subgroupName={subgroupName}
                            code={code}
                            title={title}
                            data={formData}
                            isViewMode={isViewMode}
                            fields={[
                                {
                                    name: "kode",
                                    title: "Kode",
                                    inputType: "text",
                                    dataType: (typeof formData.kode),
                                    isReadonly: false,
                                    isRequired: formData?.ucode ? true : false,
                                    onChanged: (valueObj: DataFormFieldChangeParamType<Role>) => onChanged(valueObj),
                                },
                                {
                                    name: "nama",
                                    title: "Nama",
                                    inputType: "text",
                                    dataType: (typeof formData.nama),
                                    isReadonly: false,
                                    isRequired: true,
                                    onChanged: (valueObj: DataFormFieldChangeParamType<Role>) => onChanged(valueObj),
                                },
                                {
                                    name: "level",
                                    title: "Level",
                                    inputType: "integer",
                                    dataType: (typeof formData.level),
                                    isReadonly: false,
                                    isRequired: true,
                                    onChanged: (valueObj: DataFormFieldChangeParamType<Role>) => onChanged(valueObj),
                                    minValue: 0,
                                },
                                {
                                    name: "keterangan",
                                    title: "Keterangan",
                                    inputType: "textarea",
                                    dataType: (typeof formData.keterangan),
                                    isReadonly: false,
                                    isRequired: true,
                                    onChanged: (valueObj: DataFormFieldChangeParamType<Role>) => onChanged(valueObj),
                                    rows: 3,
                                }
                            ]}
                            onValidate={onValidate}
                            onClose={(pathObject) => onClose(pathObject)}
                            onSave={(data) => onSave(data)}
                        />
                    </Grid>
                    <Grid {...menuListContainerProps}>
                        <Grid {...menuListTitleProps}>
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontWeight: "bolder",
                                }}
                            >
                                List Menu
                            </Typography>
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={formData?.list_selected_menu?.filter(m => m.can_view && m.can_add && m.can_delete && m.can_edit && m.see_other_div)?.length === freeMenuList.length}
                                        onChange={(e: any) => {
                                            if (!formData)
                                                return;

                                            setFormData({
                                                ...formData,
                                                list_selected_menu: e.target.checked ?
                                                freeMenuList.map(m => ({ucode_menu: m.ucode_menu, ucode_role: formData.ucode, kode_menu: m.kode_menu, nama_menu: m.nama_menu, can_view: e.target.checked, can_add: e.target.checked, can_edit: e.target.checked, can_delete: e.target.checked, see_other_div: e.target.checked}))
                                                :
                                                []
                                            })
                                        }}
                                    />
                                )}
                                label="Pilih Semua"
                            />
                        </Grid>
                        <Grid {...menuListProps}>
                        {
                            freeMenuList.map((m, idx) => {
                                return (
                                    <DataFormDetail<RoleMenu>
                                       
                                        index={idx}
                                        fields={[
                                            {
                                                keyField: "ucode_menu",
                                                name: "nama_menu",
                                                title: "Menu",
                                                inputType: "text",
                                                dataType: (typeof m.nama_menu),
                                                isReadonly: true,
                                                isRequired: false,
                                                gridSizeSm: 12,
                                                gridSizeMd: 12,
                                                gridSizeLg: 12,
                                                isHeader: true,
                                                hideTitle: true,
                                            },
                                            {
                                                keyField: "ucode_menu",
                                                name: "can_view",
                                                title: "Lihat Data",
                                                inputType: "checkbox",
                                                dataType: (typeof true),
                                                isReadonly: false,
                                                isRequired: false,
                                                onChanged: (valueObj: DataFormFieldChangeParamType<RoleMenu>) => onDetailMenuChanged(valueObj),
                                                gridSizeSm: 12,
                                                gridSizeMd: 12,
                                                gridSizeLg: 6,
                                            },
                                            {
                                                keyField: "ucode_menu",
                                                name: "can_add",
                                                title: "Tambah Data",
                                                inputType: "checkbox",
                                                dataType: (typeof m.can_add),
                                                isReadonly: false,
                                                isRequired: false,
                                                onChanged: (valueObj: DataFormFieldChangeParamType<RoleMenu>) => onDetailMenuChanged(valueObj),
                                                gridSizeSm: 12,
                                                gridSizeMd: 12,
                                                gridSizeLg: 6,
                                            },
                                            {
                                                keyField: "ucode_menu",
                                                name: "can_edit",
                                                title: "Ubah Data",
                                                inputType: "checkbox",
                                                dataType: (typeof m.can_edit),
                                                isReadonly: false,
                                                isRequired: false,
                                                onChanged: (valueObj: DataFormFieldChangeParamType<RoleMenu>) => onDetailMenuChanged(valueObj),
                                                gridSizeSm: 12,
                                                gridSizeMd: 12,
                                                gridSizeLg: 6,
                                            },
                                            {
                                                keyField: "ucode_menu",
                                                name: "can_delete",
                                                title: "Hapus Data",
                                                inputType: "checkbox",
                                                dataType: (typeof m.can_delete),
                                                isReadonly: false,
                                                isRequired: false,
                                                onChanged: (valueObj: DataFormFieldChangeParamType<RoleMenu>) => onDetailMenuChanged(valueObj),
                                                gridSizeSm: 12,
                                                gridSizeMd: 12,
                                                gridSizeLg: 6,
                                            },
                                            {
                                                keyField: "ucode_menu",
                                                name: "see_other_div",
                                                title: "Lihat Divisi Lain",
                                                inputType: "checkbox",
                                                dataType: (typeof m.see_other_div),
                                                isReadonly: false,
                                                isRequired: false,
                                                onChanged: (valueObj: DataFormFieldChangeParamType<RoleMenu>) => onDetailMenuChanged(valueObj),
                                                gridSizeSm: 12,
                                                gridSizeMd: 12,
                                                gridSizeLg: 6,
                                            }
                                        ]}
                                        data={formData?.list_selected_menu?.filter(d => d.ucode_menu === m.ucode_menu)[0] ?? {ucode_menu: m.ucode_menu, ucode_role: formData.ucode, kode_menu: m.kode_menu, nama_menu: m.nama_menu, can_view: false, can_add: false, can_edit: false, can_delete: false, see_other_div: false}}
                                    />
                                );
                            })
                        }
                        </Grid>
                    </Grid>
                </Grid>
            </FormContainer>
        </>
    );
};

export default Form;
